import axios, { AxiosResponse } from 'axios';
import { UseMutationResult, useQueryClient } from '@tanstack/react-query';
import { useApiMutation } from 'api/use-api-mutation';
import { API_NAMES } from 'api/constants';
import {
    AnnotationCategory,
    AnnotationCategoryRequest,
    AnnotationLayer,
    AnnotationLayerRequestWithAnnotations,
    AnnotationRequest,
    AnnotationRequestWithLayerId,
    AnnotationsApi,
    BatchDeleteRequest,
    ImportersApi,
} from 'oas/internal';
import { TfAnnotation } from './type';
import { AutoPipParameters, BatchApi } from 'oas';

export const useCreateAnnotationCategoriesBatch = (): UseMutationResult<
    AxiosResponse<AnnotationCategory[]>,
    unknown,
    AnnotationCategoryRequest[]
> => {
    const queryClient = useQueryClient();
    return useApiMutation(
        async (categoryRequest, apiConfig, axiosInstance) => {
            axiosInstance.interceptors.request.use((config) => {
                config.headers['Content-Type'] = 'application/json';
                return config;
            });
            return await new ImportersApi(apiConfig, '', axiosInstance).createAnnotationCategories(categoryRequest);
        },
        {
            onSuccess: () => {
                queryClient.invalidateQueries({
                    queryKey: ['annotation', 'categories'],
                });
            },
        },
        ['annotations-category-batch'],
        'internal',
        API_NAMES.ACTIVITY_SERVICE,
    );
};

export const useCreateAnnotationLayerForActivity = (
    activityId: string,
): UseMutationResult<AxiosResponse<AnnotationLayer>, unknown, AnnotationLayerRequestWithAnnotations, unknown> => {
    const queryClient = useQueryClient();

    return useApiMutation(
        async (categoryRequest, apiConfig, axiosInstance) => {
            axiosInstance.interceptors.request.use((config) => {
                config.headers['Content-Type'] = 'application/json';
                return config;
            });
            return await new ImportersApi(apiConfig, '', axiosInstance).createAnnotationLayerForActivity(
                activityId,
                categoryRequest,
            );
        },
        {
            onSuccess: () => {
                queryClient.invalidateQueries({
                    queryKey: ['annotation', 'layer', activityId],
                });
                queryClient.invalidateQueries({
                    queryKey: ['annotations', activityId],
                });
            },
        },
        ['annotation', 'layer', activityId],
        'internal',
        API_NAMES.ACTIVITY_SERVICE,
    );
};

export const useDeleteAnnotationLayer = (): UseMutationResult<AxiosResponse<void>, unknown, string> => {
    const queryClient = useQueryClient();
    return useApiMutation(
        async (annotationLayerId, apiConfig, axiosInstance) => {
            return await new ImportersApi(apiConfig, '', axiosInstance).deleteAnnotationLayer(annotationLayerId);
        },
        {
            onSettled: () => {
                queryClient.invalidateQueries({
                    queryKey: ['annotation', 'layer'],
                });
            },
        },
        ['annotation', 'layer'],
        'internal',
        API_NAMES.ACTIVITY_SERVICE,
    );
};

export const useDeleteAnnotations = (activityId: string): UseMutationResult<void, unknown, string[], unknown> => {
    const queryClient = useQueryClient();
    return useApiMutation(
        async (ids, apiConfig, axiosInstance) => {
            const annotationBatchRequest: BatchDeleteRequest = { ids };
            await new AnnotationsApi(apiConfig, '', axiosInstance).deleteAnnotations(annotationBatchRequest);
        },
        {
            onSuccess: () => {
                queryClient.invalidateQueries({
                    queryKey: ['annotations', activityId],
                });
            },
        },
        [],
        'internal',
        API_NAMES.ACTIVITY_SERVICE,
    );
};

export const useUpdateAnnotation = (
    activityId: string,
): UseMutationResult<void, unknown, { annotation: TfAnnotation; periodId?: string }, unknown> => {
    const queryClient = useQueryClient();
    return useApiMutation(
        async ({ annotation, periodId }, apiConfig, axiosInstance) => {
            const annotationRequest: AnnotationRequest = {
                name: annotation.name,
                start_time_ms: new Date(annotation.startDateTime).getTime(),
                end_time_ms: new Date(annotation.endDateTime).getTime(),
                athlete_ids: annotation.athleteIds,
                annotation_category_id: annotation.annotationCategory?.id,
                period_id: periodId,
            };
            await new AnnotationsApi(apiConfig, '', axiosInstance).updateAnnotation(annotation.id, annotationRequest);
        },
        {
            onSuccess: () => {
                queryClient.invalidateQueries({
                    queryKey: ['annotations', activityId],
                });
            },
        },
        [],
        'internal',
        API_NAMES.ACTIVITY_SERVICE,
    );
};

export const useCreateAnnotation = (
    activityId: string,
): UseMutationResult<void, unknown, AnnotationRequestWithLayerId, unknown> => {
    const queryClient = useQueryClient();
    return useApiMutation(
        async (annotationRequest, apiConfig, axiosInstance) => {
            await new AnnotationsApi(apiConfig, '', axiosInstance).createAnnotation(activityId, annotationRequest);
        },
        {
            onSuccess: () => {
                queryClient.invalidateQueries({
                    queryKey: ['annotations', activityId],
                });
            },
        },
        [],
        'internal',
        API_NAMES.ACTIVITY_SERVICE,
    );
};

export const useGetAutoPip = (): UseMutationResult<
    {
        statusCode: number | undefined; // Include statusCode in the return type
        status: string | undefined;
        exitCode: number | undefined;
        results: Record<string, unknown> | undefined;
    },
    unknown,
    string,
    unknown
> => {
    const queryClient = useQueryClient();

    return useApiMutation(
        async (jobName, apiConfig, axiosInstance) => {
            try {
                const response = await new BatchApi(apiConfig, '', axiosInstance).getAutoPip(jobName, {
                    validateStatus: () => true,
                });

                if (!response) {
                    return {
                        statusCode: undefined,
                        status: undefined,
                        exitCode: undefined,
                        results: undefined,
                    };
                }

                return {
                    statusCode: response.status,
                    status: response.status === 404 ? 'Not Found' : response.data?.status,
                    exitCode: response.data?.exit_code,
                    results: response.data?.results,
                };
            } catch (error) {
                if (axios.isAxiosError(error)) {
                    const statusCode = error.response?.status;

                    return {
                        statusCode,
                        status: error.response?.data?.status ?? 'Error',
                        exitCode: error.response?.data?.exit_code ?? undefined,
                        results: error.response?.data?.results ?? undefined,
                    };
                }

                // unknown error case
                return {
                    statusCode: undefined,
                    status: 'Unknown Error',
                    exitCode: undefined,
                    results: undefined,
                };
            }
        },
        {
            onSuccess: (data) => {
                if (data?.status === 'SUCCEEDED' || data?.status === 'FAILED') {
                    queryClient.invalidateQueries({
                        queryKey: ['annotations'],
                    });
                }
            },
        },
        [],
    );
};

export const usePostAutoPip = (): UseMutationResult<void, unknown, AutoPipParameters, unknown> => {
    const queryClient = useQueryClient();
    return useApiMutation(
        async (autoPipParameters, apiConfig, axiosInstance) => {
            try {
                const response = await new BatchApi(apiConfig, '', axiosInstance).postAutoPip(autoPipParameters, {
                    validateStatus: () => true,
                });

                // throw error if 4xx or 5xx status code
                if (response.status >= 400) {
                    throw new Error(`HTTP Error: ${response.status} - ${response.statusText}`);
                }
            } catch (error) {
                // Re-throw any form of error caught by the mutation
                throw error;
            }
        },
        {
            onSuccess: () => {
                queryClient.invalidateQueries({
                    queryKey: ['annotations'],
                });
            },
        },
        [],
    );
};
